import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEdit, faTrash, faPlay, faShare, faUpload, faSearch, faList, faSignInAlt, faUserShield, faCog, faClipboard } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import './QuizPanel.css';
import AuthPanel from './AuthPanel';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import axios from '../axiosConfig';
import AddAdminPanel from './AddAdminPanel';
import AlertMessage from './AlertMessage';
import InfoMessage from './InfoMessage';

const QuizPanel = ({ quizzes, isLoading, onCreateQuiz, onEditQuiz, onDeleteQuiz, onPlayQuiz, onImportQuiz, onLocalSearch, onGlobalSearch, onClose }) => {
  const [view, setView] = useState('login');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [user] = useAuthState(auth);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminQuizId, setAdminQuizId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [alert, setAlert] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [openMenus, setOpenMenus] = useState({});

  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setView('myQuizzes');
      setIsAdmin(user?.isAdmin);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inputDialogElement = document.getElementById('input-dialog-overlay');
      const confirmDialogElement = document.querySelector('.confirm-dialog-overlay');

      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        (!inputDialogElement || !inputDialogElement.contains(event.target)) &&
        (!confirmDialogElement || !confirmDialogElement.contains(event.target))
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
  };

  const showInfoMessage = (message) => {
    setInfoMessage({ message });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showAlert('success', 'Code copié dans le presse-papier !');
      })
      .catch(() => {
        showAlert('error', 'Échec de la copie du code.');
      });
  };

  const handleShareQuiz = async (quizId) => {
    try {
      const response = await axios.get(`/api/quizzes/${quizId}/share`);
      const { shareCode } = response.data;
      showInfoMessage(
        <>
          Code de partage: <span className="share-code" onClick={() => copyToClipboard(shareCode)}>{shareCode}</span> <FontAwesomeIcon icon={faClipboard} />
        </>
      );
    } catch (error) {
      showAlert('error', 'Erreur lors de la création du code de partage.');
    }
  };

  const toggleMenu = (quizId) => {
    setOpenMenus((prevOpenMenus) => {
      const newOpenMenus = {};

      if (prevOpenMenus[quizId]) {
        return newOpenMenus; // Retourne un objet vide, fermant tous les menus
      }

      // Sinon, fermer les autres menus et ouvrir celui-ci
      newOpenMenus[quizId] = true;
      return newOpenMenus;
    });
  };

  const handleLocalSearchChange = (e) => {
    setLocalSearchTerm(e.target.value);
    onLocalSearch(e.target.value);
  };

  const handleGlobalSearchChange = (e) => {
    setGlobalSearchTerm(e.target.value);
    onGlobalSearch(e.target.value);
  };

  const handleLoginSuccess = () => {
    setView('myQuizzes');
  };

  const handleDeleteQuiz = async (quizId) => {
    onDeleteQuiz(quizId);
  };

  const handleImportQuiz = async () => {
    await onImportQuiz();
    setGlobalSearchTerm('');
  };

  const uniqueQuizzes = useMemo(() => {
    const unique = quizzes
      .filter(quiz => quiz && quiz.name) 
      .reduce((acc, current) => {
        const x = acc.find(item => item.id === current.id);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);

    return unique.sort((a, b) => a.name.localeCompare(b.name));
  }, [quizzes]);

  return (
    <div className="quiz-panel-overlay">
      <div className="quiz-panel" ref={panelRef}>
        <button className="quiz-panel__close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="quiz-panel__menu-bar">
          <button className={`quiz-panel__menu-item ${view === 'login' ? 'active' : ''}`} onClick={() => setView('login')}>
            <FontAwesomeIcon icon={faSignInAlt} />  <span> Connexion</span>
          </button>
          {user && (
            <>
              <button className={`quiz-panel__menu-item ${view === 'myQuizzes' ? 'active' : ''}`} onClick={() => setView('myQuizzes')}>
                <FontAwesomeIcon icon={faList} />  <span> Mes Quiz</span>
              </button>
              <button className={`quiz-panel__menu-item ${view === 'search' ? 'active' : ''}`} onClick={() => setView('search')}>
                <FontAwesomeIcon icon={faSearch} />  <span> Rechercher</span>
              </button>
            </>
          )}
        </div>
        <div className="quiz-panel__content">
          {view === 'login' && (
            <AuthPanel onLoginSuccess={handleLoginSuccess} />
          )}
          {view === 'myQuizzes' && user && (
            <div className="quiz-panel__my-quizzes">
              <h2>Mes Quiz</h2>
              <button className="quiz-panel__create-quiz-button" onClick={onCreateQuiz}>Créer un quiz</button>
              <input 
                type="text" 
                placeholder="Rechercher parmi mes quiz..." 
                value={localSearchTerm}
                onChange={handleLocalSearchChange}
                onFocus={() => setOpenMenus({})}
                className="quiz-panel__search-input"
                ref={searchInputRef}
              />
              {isLoading ? (
                <p>Chargement...</p>
              ) : uniqueQuizzes.length > 0 ? (
                <ul className="quiz-panel__quiz-list">
                  {uniqueQuizzes.map((quiz, index) => (
                    <li key={quiz.id} className="quiz-panel__quiz-item">
                      <span title={quiz.name} onClick={() => onPlayQuiz(quiz.id)}>{quiz.name}</span>
                      <div className="quiz-panel__quiz-actions">
                        <div className="quiz-panel__actions-icons">
                          <button data-tooltip-id={`play-${quiz.id}`} data-tooltip-content="Jouer">
                            <FontAwesomeIcon icon={faPlay} onClick={() => onPlayQuiz(quiz.id)} />
                            <Tooltip id={`play-${quiz.id}`} place="bottom" effect="solid" />
                          </button>
                          {quiz.is_admin && (
                            <>
                              <button data-tooltip-id={`edit-${quiz.id}`} data-tooltip-content="Modifier">
                                <FontAwesomeIcon icon={faEdit} onClick={() => onEditQuiz(quiz.id)} />
                                <Tooltip id={`edit-${quiz.id}`} place="bottom" effect="solid" />
                              </button>
                              <button data-tooltip-id={`admin-${quiz.id}`} data-tooltip-content="Admin">
                                <FontAwesomeIcon icon={faUserShield} onClick={() => { setShowAdminPanel(true); setAdminQuizId(quiz.id); }} />
                                <Tooltip id={`admin-${quiz.id}`} place="bottom" effect="solid" />
                              </button>
                            </>
                          )}
                          <button data-tooltip-id={`share-${quiz.id}`} data-tooltip-content="Partager">
                            <FontAwesomeIcon icon={faShare} onClick={() => handleShareQuiz(quiz.id)} />
                            <Tooltip id={`share-${quiz.id}`} place="bottom" effect="solid" />
                          </button>
                          <button data-tooltip-id={`delete-${quiz.id}`} data-tooltip-content="Supprimer">
                            <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteQuiz(quiz.id, quiz.name)} />
                            <Tooltip id={`delete-${quiz.id}`} place="bottom" effect="solid" />
                          </button>
                        </div>

                        <button
                          className="quiz-panel__settings-button"
                          onClick={() => toggleMenu(quiz.id)}
                          data-tooltip-id={`settings-${quiz.id}`}
                          data-tooltip-content=""
                        >
                          <FontAwesomeIcon icon={faCog} />
                        </button>
                        <Tooltip id={`settings-${quiz.id}`} place="bottom" effect="solid" />

                        {openMenus[quiz.id] && (
                          <div className="quiz-panel__options-menu">
                            <button onClick={() => onPlayQuiz(quiz.id)}>Jouer</button>
                            {quiz.is_admin && (
                              <>
                                <button onClick={() => onEditQuiz(quiz.id)}>Modifier</button>
                                <button onClick={() => { setShowAdminPanel(true); setAdminQuizId(quiz.id); }}>Admin</button>
                              </>
                            )}
                            <button onClick={() => handleShareQuiz(quiz.id)}>Partager</button>
                            <button onClick={() => handleDeleteQuiz(quiz.id)}>Supprimer</button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No quizzes found</p>
              )}
            </div>
          )}
          {view === 'search' && user && (
            <div className="quiz-panel__search-quizzes">
              <h2>Code d'importation</h2>
              <input 
                type="text" 
                placeholder="Entrer le code d'importation..." 
                value={globalSearchTerm}
                onChange={handleGlobalSearchChange}
                className="quiz-panel__search-input"
              />
              <button className="quiz-panel__import-quiz-button" onClick={handleImportQuiz}>
                <FontAwesomeIcon icon={faUpload} /> Importer
              </button>
            </div>
          )}
          {alert && (
            <AlertMessage
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          )}
          {infoMessage && (
            <InfoMessage
              message={infoMessage.message}
              onClose={() => setInfoMessage(null)}
            />
          )}
        </div>
        {showAdminPanel && (
          <AddAdminPanel onClose={() => setShowAdminPanel(false)} quizId={adminQuizId} userEmail={user.email}/>
        )}
      </div>
    </div>
  );
};

export default QuizPanel;
